/**
 * glanceView — maps normalized WeatherData + GlanceMap → GlanceViewModel.
 *
 * CLIENT-SIDE (portable into the extension bundle — ADR-002 §5). No imports
 * from server-only modules. Pure; returns a new object.
 *
 * Glance grammar (glance-grammar-spec §1.1, §4.1):
 *   primary  : "<tempF>°"         — ≤ 8 chars; integer + degree sign
 *   delta    : "↑4° vs yesterday" — ≤ 20 chars; direction glyph + magnitude
 *   state    : condition mapping  — from §4.1 weather condition table
 *
 * Edge-case rulings (decided & tested):
 *   1. "105°" (4 chars) fits in the 8-char primary budget — no truncation.
 *   2. "-15°" (4 chars) also fits — negative prefix adds one char; still ≤ 8.
 *   3. vsYesterdayF null → delta is null (omit slot; never fake a 0 delta).
 *   4. Unknown condition codes map to "neutral" (never crash).
 *   5. Extreme heat (>100°F) or cold (<10°F) → "alert" regardless of sky condition.
 *   6. glance.state = null → always "neutral" (config explicitly suppresses state).
 */

import type { GlanceMap } from 'briefpane/schema';
import type { GlanceState, GlanceViewModel } from 'briefpane/contracts';
import type { WeatherData } from './normalize.js';

// ---------------------------------------------------------------------------
// OWM condition code → glance state (§4.1 weather condition mapping table)
// ---------------------------------------------------------------------------

/**
 * OWM groups its condition codes into ranges (not individual values):
 *   2xx = Thunderstorm, 3xx = Drizzle, 5xx = Rain, 6xx = Snow,
 *   7xx = Atmosphere (mist/fog/haze/etc.), 800 = Clear, 80x = Clouds.
 *
 * We export this registry so tests can assert the groupings without re-implementing
 * the logic, and future maintainers can update it in one place.
 *
 * Key = the start of each OWM code group (inclusive), value = glance state.
 * We check the MOST specific match (exact code first, then range).
 */
export const OWM_CONDITION_CODE_GROUPS: Readonly<Record<number, GlanceState>> = {
  // Thunderstorm (200–232) — "negative" (high urgency per glance-spec §4.1)
  200: 'negative',
  // Drizzle (300–321) — "negative" (rain family)
  300: 'negative',
  // Rain (500–531) — "negative"
  500: 'negative',
  // Snow (600–622) — "alert"
  600: 'alert',
  // Atmosphere — 700-781: Fog (741), Mist (701), Haze (721), Smoke (711), etc. → "alert"
  700: 'alert',
  // Clear (800) — "neutral"
  800: 'neutral',
  // Clouds (801–804) — "neutral" (overcast included per spec)
  801: 'neutral',
};

/**
 * Map an OWM weather condition string → GlanceState (glance-spec §4.1).
 *
 * Condition string fallback is used when a condition code is not available or
 * falls outside the known ranges. The string match covers the main OWM keywords.
 */
export function conditionToGlanceState(condition: string, tempF: number): GlanceState {
  // Extreme temperature overrides (glance-spec §4.1):
  //   > 100°F / 38°C → "alert"
  //   < 10°F / -12°C → "alert"
  if (tempF > 100 || tempF < 10) return 'alert';

  const c = condition.toLowerCase();
  if (c.includes('thunder')) return 'negative';
  if (c.includes('rain') || c.includes('drizzle')) return 'negative';
  if (c.includes('snow') || c.includes('sleet') || c.includes('hail')) return 'alert';
  if (c.includes('fog') || c.includes('mist') || c.includes('haze') || c.includes('smoke')) {
    return 'alert';
  }
  // Clear, Clouds, Overcast → neutral
  return 'neutral';
}

// ---------------------------------------------------------------------------
// glanceView implementation
// ---------------------------------------------------------------------------

/**
 * Map normalized WeatherData + the tile's GlanceMap → GlanceViewModel.
 *
 * CLIENT-SIDE. Pure. Returns a new object every call (immutable).
 *
 * @param data   normalized weather metrics (from normalize())
 * @param glance validated GlanceMap from the tile config
 */
export function glanceView(data: WeatherData, glance: GlanceMap): GlanceViewModel {
  // --- primary slot ("72°") ---
  const primary = `${data.tempF}°`;

  // --- delta slot ---
  // Omit when: config says null OR no yesterday data available.
  let delta: string | null = null;
  let direction: 'up' | 'down' | 'flat' | undefined;

  if (glance.delta !== null && data.vsYesterdayF !== null) {
    const arrow = data.vsYesterdayF >= 0 ? '↑' : '↓';
    direction = data.vsYesterdayF > 0 ? 'up' : data.vsYesterdayF < 0 ? 'down' : 'flat';
    delta = `${arrow}${Math.abs(data.vsYesterdayF)}° vs yesterday`;
  }

  // --- glance state ---
  let glanceState: GlanceState = 'neutral';
  if (glance.state !== null) {
    glanceState = conditionToGlanceState(data.condition, data.tempF);
  }

  return {
    dataState: 'ok',
    glanceState,
    slots: {
      primary,
      delta,
      ...(direction !== undefined ? { direction } : {}),
    },
    label: null, // label comes from the tile config, not the adapter
    fetchedAt: null, // fetchedAt comes from the cache envelope
  };
}
